import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import axios from "axios";
import MicrosoftSSOLogin from "./MicrosoftSSO";
import GoogleSSOLogin from "./GoogleSSO";
import useApplicationData from "../../hooks/useApplicationData";
import { API_BASE_URL } from "../../config/config";

export default function SignInForm () {
  const [loading, setLoading] = useState(false);
  const [microsoftUser, setMicrosoftUser] = useState(null);
  const [googleUser, setGoogleUser] = useState(null);
  const navigate = useNavigate();
  const {
    fetchApplicationData,
    fetchTotalApplicationsCount,
    fetchUniqueValues
  } = useApplicationData();

  const login = useCallback(
    async (email, password, name, picture) => {
      setLoading(true);
      try {
        const formData = new URLSearchParams();
        formData.append("username", email);
        formData.append("password", password);

        const response = await axios.post(
          `${API_BASE_URL}/insureai/ml/v1/login`,
          formData,
          {
            headers: {
              "Content-Type": "application/x-www-form-urlencoded",
              Accept: "application/json"
            }
          }
        );

        const { access_token, refresh_token } = response.data;
        localStorage.setItem("access_token", access_token);
        localStorage.setItem("refresh_token", refresh_token);
        localStorage.setItem(
          "userDetails",
          JSON.stringify({ name: name || "testuser", picture: picture || "" })
        );
        await fetchApplicationData(access_token, refresh_token);
        await fetchTotalApplicationsCount(access_token, refresh_token);
        await fetchUniqueValues(access_token, refresh_token);

        navigate("/app/details");
      } catch (error) {
        console.error("Incorrect username or password. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [
      navigate,
      fetchApplicationData,
      fetchTotalApplicationsCount,
      fetchUniqueValues
    ]
  );

  useEffect(() => {
    if (microsoftUser) {
      login(microsoftUser.email, microsoftUser.password);
    }
  }, [microsoftUser, login]);

  useEffect(() => {
    // login("testuser", "Python@123");
    if (googleUser) {
      login(
        googleUser.email,
        googleUser.password,
        googleUser.name,
        googleUser.picture
      );
    }
  }, [googleUser, login]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4 sm:px-6 lg:px-8">
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden max-w-6xl w-full mx-auto my-4">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left side - Login Form */}
          <div className="p-8 sm:p-12 flex flex-col justify-center">
            <div className="text-center">
              <h2 className="text-3xl font-extrabold text-gray-900">
                Welcome Back
              </h2>
              <p className="mt-2 text-sm text-gray-600">
                Sign in to your account to continue
              </p>
            </div>

            <div className="mt-8 space-y-6">
              <div className="space-y-4">
                <GoogleSSOLogin
                  setGoogleUser={setGoogleUser}
                  className="w-full"
                />

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-gray-200"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="px-4 bg-white text-gray-500">
                      Or continue with
                    </span>
                  </div>
                </div>

                <MicrosoftSSOLogin
                  setMicrosoftUser={setMicrosoftUser}
                  className="w-full"
                />
              </div>
            </div>
          </div>

          {/* Right side - Image */}
          <div className="hidden lg:block relative h-[500px] bg-gray-50">
            <img
              src="/img/login-img.png"
              alt="Login illustration"
              className="absolute inset-0 w-full h-full object-cover"
            />
          </div>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Loader2 className="w-12 h-12 text-white animate-spin" />
          </div>
        )}
      </div>
    </div>
  );
}
